/**
 * techApi — Sprint 6.2 (App Técnico / Mobile Installer).
 *
 * Cliente HTTP do App Técnico. Separado do api.js do NOC de
 * propósito: o app mobile usa o header X-Tech-Token (token de menor
 * privilégio), nunca o admin token.
 *
 * O token técnico é guardado em memória de sessão (sessionStorage):
 * some quando o técnico fecha o navegador. É o suficiente para o uso
 * em campo e evita deixar credencial persistida no aparelho.
 */

const TOKEN_KEY = "ipwd_tech_token";

/** Lê o token técnico da sessão. */
export function getTechToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

/** Guarda o token técnico na sessão. */
export function setTechToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ambiente sem sessionStorage — ignora */
  }
}

// ── STOCK-SONOFF-INVENTORY-7 — conta de técnico (opcional) ──────────
// Bearer de um usuário role='field'. Complementa (NÃO substitui) o
// X-Tech-Token: com ele o backend vincula a unidade instalada ao
// técnico (assigned_user_id) e o app mostra "Meu estoque".

const BEARER_KEY = "ipwd_tech_bearer";
const BEARER_EMAIL_KEY = "ipwd_tech_bearer_email";

export function getFieldBearer() {
  try {
    return sessionStorage.getItem(BEARER_KEY) || "";
  } catch {
    return "";
  }
}

export function getFieldEmail() {
  try {
    return sessionStorage.getItem(BEARER_EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

export function setFieldBearer(token, email) {
  try {
    if (token) {
      sessionStorage.setItem(BEARER_KEY, token);
      sessionStorage.setItem(BEARER_EMAIL_KEY, email || "");
    } else {
      sessionStorage.removeItem(BEARER_KEY);
      sessionStorage.removeItem(BEARER_EMAIL_KEY);
    }
  } catch {
    /* ambiente sem sessionStorage — ignora */
  }
}

export class TechApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "TechApiError";
    this.status = status;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const headers = { "X-Tech-Token": getTechToken() };
  // STOCK-7 — Bearer field opcional junto do tech token (dual-accept):
  // habilita o vínculo técnico↔unidade no finish e o "Meu estoque".
  const bearer = getFieldBearer();
  if (bearer) headers["Authorization"] = `Bearer ${bearer}`;
  const init = { method, headers, signal };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(path, init);
  } catch (e) {
    throw new TechApiError(
      "Falha de rede — verifique a conexão.", 0
    );
  }
  const ctype = res.headers.get("content-type") || "";
  const data = ctype.includes("application/json")
    ? await res.json().catch(() => null)
    : null;
  if (!res.ok) {
    let detail = (data && (data.detail || data.message))
      || `Erro ${res.status}`;
    // STOCK-9 (review): detail pode ser objeto (ex.: 403 de posse
    // {code, message}) — achata p/ não virar "[object Object]".
    if (detail && typeof detail === "object") {
      detail = detail.message || detail.code || JSON.stringify(detail);
    }
    throw new TechApiError(detail, res.status);
  }
  return data;
}

// ── Endpoints /api/tech/* ────────────────────────────────────────────

/** Valida o token abrindo uma sessão de teste leve (workorders). */
export async function techPing({ signal } = {}) {
  return request("/api/tech/workorders?limit=1", { signal });
}

export async function startSession(installer, teamId, { signal } = {}) {
  return request("/api/tech/sessions", {
    method: "POST", signal,
    body: { installer, team_id: teamId || null },
  });
}

export async function getSession(sessionId, { signal } = {}) {
  return request(
    `/api/tech/sessions/${encodeURIComponent(sessionId)}`,
    { signal }
  );
}

export async function scanQr(sessionId, qrSerial, { signal } = {}) {
  return request("/api/tech/scan", {
    method: "POST", signal,
    body: { session_id: sessionId, qr_serial: qrSerial },
  });
}

export async function searchCustomers(q, { signal } = {}) {
  return request(
    `/api/tech/customer-search?q=${encodeURIComponent(q)}`,
    { signal }
  );
}

export async function selectCustomer(payload, { signal } = {}) {
  return request("/api/tech/select-customer", {
    method: "POST", signal, body: payload,
  });
}

export async function captureLocation(payload, { signal } = {}) {
  return request("/api/tech/location", {
    method: "POST", signal, body: payload,
  });
}

export async function listWorkOrders(installer, { signal } = {}) {
  const qs = installer
    ? `?installer=${encodeURIComponent(installer)}`
    : "";
  return request(`/api/tech/workorders${qs}`, { signal });
}

export async function finishInstall(payload, { signal } = {}) {
  return request("/api/tech/install", {
    method: "POST", signal, body: payload,
  });
}

// ── STOCK-SONOFF-INVENTORY-7 — conta de técnico + Meu estoque ────────

/** Login da conta de técnico (role=field) → guarda o Bearer da sessão. */
export async function fieldLogin(email, password, { signal } = {}) {
  const res = await fetch("/api/auth/login", {
    method: "POST", signal,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new TechApiError(
      (data && (data.detail || data.message)) || `Erro ${res.status}`,
      res.status,
    );
  }
  if (data.role !== "field") {
    throw new TechApiError(
      "Esta conta não é de técnico de campo (role=field).", 403,
    );
  }
  setFieldBearer(data.token, data.email);
  return data;
}

/** GET /api/installer/inventory/mine — unidades do técnico logado. */
export async function getMyInventory({ signal } = {}) {
  return request("/api/installer/inventory/mine", { signal });
}
